import { z } from 'zod';
import {
  loanPurposeEnum,
  propertyStatusEnum,
  employmentStatusEnum,
  employmentFrequencyEnum,
  educationLevelEnum
} from './form';

// API Response Schemas
export const apiStatusSchema = z.object({
  code: z.number(),
  message: z.string()
});

export const connectResponseSchema = z.object({
  status: apiStatusSchema,
  spinwheelId: z.string().optional(),
  hubspotRecordId: z.string().optional(),
  verificationStarted: z.boolean().optional(),
  kbaAvailable: z.boolean().optional()
});

export const createContactResponseSchema = z.object({
  spinwheelStatus: z.enum(['QUALIFIED', 'UNQUALIFIED']).optional(),
  experianStatus: z.enum(['QUALIFIED', 'UNQUALIFIED']).optional(),
  firstName: z.string(),
  state: z.enum(['accepted', 'rejected']),
  status: apiStatusSchema,
  debtAmount: z.number().optional(),
  debtType: z.enum(['debt', 'debt2', 'none']).optional()
});

export const submitApplicationResponseSchema = z.object({
  status: apiStatusSchema,
  storeUuid: z.string().optional(),
  offerId: z.string().optional(),
  offerCount: z.number().optional(),
  redirectUrl: z.string().optional()
});

export const contactCenterAvailabilityLevelSchema = z.enum(['none', 'low', 'high']);

export const contactCenterAvailabilityResponseSchema = z.object({
  status: apiStatusSchema,
  capacity: z.number(),
  shouldSendCall: z.boolean(),
  availability: contactCenterAvailabilityLevelSchema.optional()
});

export const scheduleAppointmentResponseSchema = z.object({
  status: apiStatusSchema
});

export const connectCallResponseSchema = z.object({
  status: apiStatusSchema,
  from: z.string().optional()
});

export const timeslotAvailabilitySchema = z.object({
  booked: z.number(),
  capacity: z.number(),
  isAvailable: z.boolean(),
  slotsAvailable: z.number()
});

export const availabilityResponseSchema = z.object({
  status: apiStatusSchema,
  totalCalls: z.number(),
  data: z.record(z.string(), timeslotAvailabilitySchema)
});

// Prospect identity verification response
export const prospectVerifyResponseSchema = z.object({
  status: apiStatusSchema,
  prospectStatus: z.enum(['QUALIFIED', 'UNQUALIFIED', 'UNVERIFIED', 'NOT FOUND', 'ERROR']),
  debtAmount: z.number().optional(),
  firstName: z.string().optional(),
  verifiedVia: z.string().optional()
});

// New: Thank You (Success) tracking response
export const successPageResponseSchema = z.object({
  status: apiStatusSchema
});

// KBA (Knowledge-Based Authentication) schemas
export const kbaOptionSchema = z.object({
  id: z.string(),
  text: z.string()
});

export const kbaQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  options: z.array(kbaOptionSchema)
});

export const connectKbaResponseSchema = z.object({
  status: apiStatusSchema,
  spinwheelId: z.string().optional(),
  hubspotRecordId: z.string().optional(),
  kbaQuestions: z.array(kbaQuestionSchema).optional(),
  connectionStatus: z.string().optional()
});

export const verifyKbaResponseSchema = z.object({
  status: apiStatusSchema,
  kbaQuestions: z.array(kbaQuestionSchema).optional(),
  connectionStatus: z.string().optional()
});

export type ApiStatus = z.infer<typeof apiStatusSchema>;
export type ConnectResponse = z.infer<typeof connectResponseSchema>;
export type CreateContactResponse = z.infer<typeof createContactResponseSchema>;
export type SubmitApplicationResponse = z.infer<typeof submitApplicationResponseSchema>;
export type ContactCenterAvailabilityLevel = z.infer<typeof contactCenterAvailabilityLevelSchema>;
export type ContactCenterAvailabilityResponse = z.infer<typeof contactCenterAvailabilityResponseSchema>;
export type ScheduleAppointmentResponse = z.infer<typeof scheduleAppointmentResponseSchema>;
export type ConnectCallResponse = z.infer<typeof connectCallResponseSchema>;
export type TimeslotAvailability = z.infer<typeof timeslotAvailabilitySchema>;
export type AvailabilityResponse = z.infer<typeof availabilityResponseSchema>;
export type ProspectVerifyResponse = z.infer<typeof prospectVerifyResponseSchema>;
export type SuccessPageResponse = z.infer<typeof successPageResponseSchema>;
export type KbaOption = z.infer<typeof kbaOptionSchema>;
export type KbaQuestion = z.infer<typeof kbaQuestionSchema>;
export type ConnectKbaResponse = z.infer<typeof connectKbaResponseSchema>;
export type VerifyKbaResponse = z.infer<typeof verifyKbaResponseSchema>;

// Partner pre-fill data fetched by partner_id query param
export const partnerDataSchema = z.object({
  loanAmount: z.number().positive().optional(),
  loanPurpose: loanPurposeEnum.optional(),
  employmentStatus: employmentStatusEnum.optional(),
  employmentFrequency: employmentFrequencyEnum.optional(),
  annualIncome: z.number().positive().optional(),
  propertyStatus: propertyStatusEnum.optional(),
  educationLevel: educationLevelEnum.optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  dob: z.string().optional()
});

export const partnerPreloadResponseSchema = z.object({
  status: apiStatusSchema,
  data: partnerDataSchema
});

export type PartnerData = z.infer<typeof partnerDataSchema>;
export type PartnerPreloadResponse = z.infer<typeof partnerPreloadResponseSchema>;

// Auto-Echelon
export const startAutoEchelonResponseSchema = z.object({
  status: apiStatusSchema,
  opportunityId: z.string().optional(),
  dealId: z.string().optional(),
  started: z.boolean().optional()
});

export const getOpportunityStatusResponseSchema = z.object({
  status: apiStatusSchema,
  aeClientLink: z.string().nullable(),
  completed: z.boolean(),
  succeeded: z.boolean(),
  redirectUrl: z.string().optional()
});

export const bffAutoEchelonStatusResponseSchema = z.object({
  status: apiStatusSchema,
  completed: z.boolean(),
  succeeded: z.boolean(),
  redirectUrl: z.string().nullish()
});

export type StartAutoEchelonResponse = z.infer<typeof startAutoEchelonResponseSchema>;
export type GetOpportunityStatusResponse = z.infer<typeof getOpportunityStatusResponseSchema>;

// Backend sometimes returns booleans as the strings "true"/"false"
const stringyBooleanSchema = z.union([z.boolean(), z.string()]).transform(value => value === true || value === 'true');

// Lead details fetched by a unique lead id (e.g. from a `useruniqueId` query param)
export const leadDetailsDataSchema = z.object({
  hubspotRecordId: z.string().optional(),
  firstName: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  debtAmount: z.number(),
  applicationStatus: z.string(),
  createdAt: z.string().optional(),
  isBpoAgent: stringyBooleanSchema,
  consentDateTime: z.string().optional()
});

export const leadDetailsResponseSchema = z.object({
  data: leadDetailsDataSchema
});

export type LeadDetailsData = z.infer<typeof leadDetailsDataSchema>;
export type LeadDetailsResponse = z.infer<typeof leadDetailsResponseSchema>;

// API Error
export class ApiError extends Error {
  constructor(
    message: string,
    public status: ApiStatus
  ) {
    super(message);
    this.name = 'ApiError';
  }
}